import { ReactNode } from 'react';

import { Applicant, DecisionDetailLink } from '@/components/pages/Applicant/Applicant.types';
import {
    ProductNameFields,
    ProductNames,
} from '@/components/providers/ApplicantContextProvider/ApplicantContextProvider.types';
import { formatOrdinal } from '@/components/shared/utils/utils';

import { getSubmittedOrdinals } from '../../Applicant.utils';
import { getProductLabel } from '../ApplicantConditions/ApplicantConditions.utils';
import { ApplicationSection } from '../ApplicationView';
import { ApplicantSubmissionSummary } from './ApplicantSubmissions.types';
import { CURRENT_APPLICATION_VALUE } from './constants';

// One label per product whose Submitted status links this submission, such as "Factoring: Submitted (2nd time)".
// The count comes from the live applicant, because the payload holds no product statuses.
export const getSubmittedStatusLabels = (
    applicant: Applicant | undefined,
    productNames: ProductNames[],
    applicantSubmissionId: string,
): string[] =>
    productNames.flatMap((product) => {
        const links = (
            applicant?.[ProductNameFields[product]] as {
                decisionDetailLinks?: DecisionDetailLink[];
            }
        )?.decisionDetailLinks;
        const ordinals = getSubmittedOrdinals(links);
        const linked = links?.find(
            ({ decisionDetail }) =>
                decisionDetail.applicantSubmissionId === applicantSubmissionId &&
                ordinals.has(decisionDetail.id),
        );

        return linked
            ? [
                  `${getProductLabel(product)}: Submitted (${formatOrdinal(ordinals.get(linked.decisionDetail.id)!)} time)`,
              ]
            : [];
    });

// Newest first, with the id breaking a tie on the same time, so the order never depends on how the list arrives.
const newestFirst = (submissions: ApplicantSubmissionSummary[]) =>
    [...submissions].sort(
        (a, b) => Date.parse(b.dateCreated) - Date.parse(a.dateCreated) || b.id.localeCompare(a.id),
    );

// The live application stands in for the newest submission, so the pulldown lists only the older ones.
export const splitNewestSubmission = (
    submissions: ApplicantSubmissionSummary[],
): { newest: ApplicantSubmissionSummary | undefined; older: ApplicantSubmissionSummary[] } => {
    const [newest, ...older] = newestFirst(submissions);

    return { newest, older };
};

// The submission recorded just before the given one; the live application stands in for the newest, as its
// label says. An id that isn't in the list has no previous submission.
export const getPreviousSubmissionId = (
    submissions: ApplicantSubmissionSummary[],
    value: string,
): string | null => {
    const ordered = newestFirst(submissions);
    const index =
        value === CURRENT_APPLICATION_VALUE ? 0 : ordered.findIndex(({ id }) => id === value);

    return index === -1 ? null : (ordered[index + 1]?.id ?? null);
};

export interface AlignedSection {
    key: string;
    left: ReactNode;
    right: ReactNode;
}

// One row per section either side holds, in page order, so the same section sits level on both sides.
export const alignApplicationSections = (
    left: ApplicationSection[],
    right: ApplicationSection[],
): AlignedSection[] => {
    const rows = new Map<string, AlignedSection & { order: number }>();
    const add = (sections: ApplicationSection[], side: 'left' | 'right') =>
        sections.forEach(({ key, order, node }) => {
            const row = rows.get(key) ?? { key, order, left: null, right: null };
            row[side] = node;
            rows.set(key, row);
        });

    add(left, 'left');
    add(right, 'right');

    return [...rows.values()]
        .sort((a, b) => a.order - b.order)
        .map(({ key, left: leftNode, right: rightNode }) => ({
            key,
            left: leftNode,
            right: rightNode,
        }));
};
